import { Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, catchError, switchMap } from 'rxjs/operators';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { environment } from '../../../../environment/environment';
import { ParticipationEventDTO } from '../../../../dto/event/participation-event.dto';
import { UserService } from '../../../user/user.service';

// Helper functions
function getStatusDisplay(status: string): string {
  const statusMap: { [key: string]: string } = {
    'registered': 'Tilmeldt',
    'completed': 'Gennemført',
    'cancelled': 'Aflyst',
    'cancellation': 'Afbud',
    'no_show': 'Udeblevet',
    'not_appear': 'Udeblevet',
    'waitlist': 'Venteliste',
    'pending': 'Tilmeldt',
    'shownup': 'Mødt op',
    'requires_attention': 'Kræver opmærksomhed',
    'not_participant': 'Ikke deltager',
    'assigned': 'Tildelt via gruppe'
  };
  return statusMap[status] || status;
}

function getYearOptions() {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = currentYear; i >= currentYear - 5; i--) {
    years.push({ value: i.toString(), label: i.toString() });
  }
  return [{ value: '', label: 'Alle år' }, ...years];
}

const PROFILE_UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function uuidFromUrl(url: string): string | null {
  const withoutHash = url.split('#')[0] ?? '';
  const [path, query = ''] = withoutHash.split('?');
  const segments = path.split('/').filter(Boolean).map(segment => {
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  });

  const profileIndex = segments.findIndex(segment => segment.toLowerCase() === 'profile');
  if (profileIndex !== -1) {
    const afterProfile = segments[profileIndex + 1];
    if (afterProfile && PROFILE_UUID_PATTERN.test(afterProfile)) {
      return afterProfile;
    }
  }

  const queryParams = new URLSearchParams(query);
  const fromQuery = queryParams.get('userUuid') ?? queryParams.get('user');
  if (fromQuery && PROFILE_UUID_PATTERN.test(fromQuery)) {
    return fromQuery;
  }

  const pathUuid = [...segments].reverse().find(segment => PROFILE_UUID_PATTERN.test(segment));
  return pathUuid ?? null;
}

function resolveProfileUserUuid(
  params: LoadDataParams | undefined,
  router: Router,
  userService: UserService
): string | null {
  const fromLocation = typeof window !== 'undefined'
    ? uuidFromUrl(`${window.location.pathname}${window.location.search}`)
    : null;
  if (fromLocation) {
    return fromLocation;
  }

  const fromRouter = uuidFromUrl(router.url);
  if (fromRouter) {
    return fromRouter;
  }

  const fromParams = params?.queryParams?.['userUuid'];
  if (typeof fromParams === 'string' && PROFILE_UUID_PATTERN.test(fromParams)) {
    return fromParams;
  }

  return userService.getCurrentUserUuid();
}

function formatDateTimeRange(startDate: string, endDate?: string): string {
  const start = new Date(startDate);
  const startDateStr = start.toLocaleDateString('da-DK');
  const startTimeStr = start.toLocaleTimeString('da-DK', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  if (!endDate) {
    return `${startDateStr} ${startTimeStr}`;
  }

  const end = new Date(endDate);
  const endDateStr = end.toLocaleDateString('da-DK');
  const endTimeStr = end.toLocaleTimeString('da-DK', { 
    hour: '2-digit', 
    minute: '2-digit' 
  });

  // If same date, show: "DD/MM/YYYY HH:MM - HH:MM"
  if (startDateStr === endDateStr) {
    return `${startDateStr} ${startTimeStr} - ${endTimeStr}`;
  }
  
  // If different dates, show: "DD/MM/YYYY HH:MM - DD/MM/YYYY HH:MM"
  return `${startDateStr} ${startTimeStr} - ${endDateStr} ${endTimeStr}`;
}

export function profile_user_events(
  componentDataSourceService: ComponentDataSourceService,
  http: HttpClient,
  router: Router,
  userService: UserService
) {
    // Define the fetchUserEvents function outside the returned object
    const fetchUserEvents = (userUuid: string, params: LoadDataParams | undefined) => {
      return componentDataSourceService.loadDataWithStandardFilters(
        'profile_user_events',
        `/users/${userUuid}/events`,
        params ? { ...params, queryParams: { ...params.queryParams, userUuid } } : { queryParams: { userUuid } }
      ).pipe(
        catchError(error => {
          console.error('Error fetching user events:', error?.status, error?.error ?? error?.message);
          return of({ items: [], totalCount: 0 });
        }),
        map(result => {
          const events = (result.items ?? []).map((eventData: any) => {
            const participation_status = eventData.participation_status || 'registered';

            return {
              uuid: eventData.uuid,
              name: eventData.name,
              date: eventData.date,
              end_date: eventData.end_date,
              date_time: formatDateTimeRange(eventData.date, eventData.end_date),
              participation_status,
              status_display: getStatusDisplay(participation_status),
              location_name: eventData.location_name || 'TBA',
              user_uuid: userUuid,
              has_certificate: participation_status === 'completed'
            };
          });

          return {
            items: events,
            totalCount: result.totalCount ?? events.length,
            pageSize: result.pageSize ?? 50,
            pageIndex: result.pageIndex ?? 0,
            user_uuid: userUuid
          };
        })
      );
    };

    return {
      name: 'User Events',
      description: 'Liste af alle events for en bruger',
      execute: (params?: LoadDataParams) => {
        const userUuid = resolveProfileUserUuid(params, router, userService);

        if (!userUuid) {
          return userService.getCurrentUserFromToken().pipe(
            switchMap(currentUser => {
              if (!currentUser || !currentUser.uuid) {
                console.error('No current user or UUID available');
                return of({ items: [], totalCount: 0 });
              }

              return fetchUserEvents(currentUser.uuid, params);
            }),
            catchError(error => {
              console.error('Error getting current user from token:', error);
              return of({ items: [], totalCount: 0 });
            })
          );
        }

        return fetchUserEvents(userUuid, params);
      },
      settings: (() => {
        const baseColumns = [
          { 
            key: 'name', 
            header: 'Event', 
            type: 'text',
            sortable: true,
            fill: true,
          },
          { 
            key: 'date_time', 
            header: 'Dato & Tid', 
            type: 'text',
            sortable: true
          },
          { 
            key: 'location_name', 
            header: 'Lokation', 
            type: 'text',
            sortable: true
          },
          { 
            key: 'participation_status', 
            header: 'Status', 
            type: 'text',
            sortable: true,
            transform: (value: string) => getStatusDisplay(value)
          },
          {
            key: 'actions',
            header: '',
            type: 'button',
            buttonText: 'Åbn Event',
            buttonColor: 'primary',
            alignment: 'right',
            fitContent: true,
            minWidth: '108px',
            maxWidth: '120px',
            variant: 'raised',
            action: (row: any) => {
              const eventUrl = `${window.location.origin}/event/${row.uuid}`;
              window.open(eventUrl, '_blank');
            }
          }
        ];

        // Always include the certificate button column
        // The action will handle the logic for when it's available
        const certificateColumn = {
          key: 'certificate_download',
          header: '',
          type: 'button',
          buttonText: 'Hent Certifikat',
          buttonColor: 'accent',
          alignment: 'right',
          fitContent: true,
          minWidth: '120px',
          maxWidth: '136px',
          variant: 'raised',
          showIf: (row: any) => row.has_certificate === true,
          action: (row: any) => {
            const certificateUrl = `${environment.apiUrl}/users/${row.user_uuid}/events/${row.uuid}/certificate`;
            window.open(certificateUrl, '_blank');
          }
        };

        const allColumns = [...baseColumns, certificateColumn];

        const settings = {
          columns: allColumns,
          pageable: true,
          sortable: true,
          rowHover: true,
          searchable: true,
          tableLayout: 'auto' as const,
          defaultSortColumn: 'date',
          defaultSortDirection: 'desc',
          filters: [
            {
              key: 'year',
              label: 'År',
              type: 'select',
              options: getYearOptions()
            },
            {
              key: 'status',
              label: 'Status',
              type: 'select',
              options: [
                { value: '', label: 'Alle' },
                { value: 'registered', label: 'Tilmeldt' },
                { value: 'completed', label: 'Gennemført' },
                { value: 'cancelled', label: 'Aflyst' },
                { value: 'cancellation', label: 'Afbud' },
                { value: 'no_show', label: 'Udeblevet' },
                { value: 'not_appear', label: 'Udeblevet' },
                { value: 'waitlist', label: 'Venteliste' },
                { value: 'pending', label: 'Tilmeldt' },
                { value: 'shownup', label: 'Mødt op' },
                { value: 'requires_attention', label: 'Kræver opmærksomhed' },
                { value: 'not_participant', label: 'Ikke deltager' },
                { value: 'assigned', label: 'Tildelt via gruppe' }
              ]
            }
          ]
        };
        
        return settings;
      })()
    };
} 